import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import type { Group, Status } from "@1js/pl-types";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { NoResults } from "../NoResults/NoResults";
import type { Prompt } from "../data";
import {
  PromptGridDescription,
  PromptGridLearnMoreLinkLabel,
  PromptGridNoTeamsSuccessButtonLabel,
  PromptGridNoTeamsSuccessDescription,
  PromptGridNoTeamsSuccessTitle,
  PromptGridTeamsNoPromptsMemberSuccessButtonLabel,
  PromptGridTeamsNoPromptsMemberSuccessDescription,
  PromptGridTeamsNoPromptsMemberSuccessTitle,
  PromptGridTeamsNoPromptsOwnerSuccessButtonLabel,
  PromptGridTeamsNoPromptsOwnerSuccessDescription,
  PromptGridTeamsNoPromptsOwnerSuccessTitle,
  PromptsRequestFailed,
} from "./PromptResultsAnnouncement.strings";

const useStyles = makeStyles({
  srOnly: {
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: "auto",
    position: "absolute",
    width: "1px",
    whiteSpace: "nowrap",
    ...shorthands.border("0px"),
    ...shorthands.margin("0px"),
    ...shorthands.overflow("hidden"),
    ...shorthands.padding("0px"),
  },

  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("20px", "0px"),
    "@media (max-width: 540px)": {
      ...shorthands.padding("16px", "0px"),
    },
  },
  text: {
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "28px",
    zIndex: 1,
    "@media (max-width: 540px)": {
      fontSize: "16px",
      lineHeight: "22px",
    },
  },
  additionalText: {
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightRegular,
    lineHeight: "28px",
    zIndex: 1,
    "@media (max-width: 540px)": {
      fontSize: "16px",
      lineHeight: "22px",
    },
  },
  actionButton: {
    marginTop: "20px",
    color: tokens.colorBrandForeground1,
    ...shorthands.border("1px", "solid", tokens.colorBrandForeground1),
  },
});
type Props = {
  id: string;
  prompts: Prompt[];
  isLoading?: boolean;
  isFailed?: boolean;
  showFavoritesOnly?: boolean;
  hasBookmarkedPrompts?: boolean;
  isShowGroupPrompts?: boolean;
  groups?: Group[];
  groupPrompts?: Prompt[];
  onExploreOtherPrompts?: () => void;
  onSharePrompts?: () => void;
  groupStatus?: Status;
  groupPromptStatus?: Status;
};

export const PromptResultsAnnouncement: React.FC<Props> = ({
  id,
  prompts,
  isLoading,
  isFailed,
  hasBookmarkedPrompts,
  showFavoritesOnly,
  groups,
  isShowGroupPrompts,
  groupPrompts,
  onExploreOtherPrompts,
  onSharePrompts,
  groupStatus,
  groupPromptStatus,
}) => {
  const styles = useStyles();
  const localization = useStrings({
    PromptsRequestFailed,
    PromptGridDescription,
    PromptGridNoTeamsSuccessButtonLabel,
    PromptGridNoTeamsSuccessTitle,
    PromptGridTeamsNoPromptsMemberSuccessTitle,
    PromptGridTeamsNoPromptsMemberSuccessDescription,
    PromptGridTeamsNoPromptsMemberSuccessButtonLabel,
    PromptGridTeamsNoPromptsOwnerSuccessButtonLabel,
    PromptGridTeamsNoPromptsOwnerSuccessTitle,
    PromptGridTeamsNoPromptsOwnerSuccessDescription,
    PromptGridLearnMoreLinkLabel,
  });
  const localizationWithPlaceholders = useStringsWithPlaceholders({
    PromptGridNoTeamsSuccessDescription,
  });

  return (
    <div aria-live="polite" aria-busy={isLoading}>
      {!isLoading &&
        !isFailed &&
        prompts.length === 0 &&
        !isShowGroupPrompts && (
          <NoResults
            showBookmarkOnly={showFavoritesOnly}
            hasBookmarkedPrompts={hasBookmarkedPrompts}
          />
        )}
      {((!isLoading &&
        isFailed &&
        !isShowGroupPrompts &&
        prompts.length === 0) ||
        (groupStatus === "failed" && isShowGroupPrompts) ||
        (groupPromptStatus === "failed" && isShowGroupPrompts)) && (
        <div className={styles.container}>
          <div className={styles.text}>
            {localization.PromptsRequestFailed()}
          </div>
        </div>
      )}
      {!isLoading && !isFailed && prompts.length > 0 && (
        <div id={id} className={styles.srOnly}>
          {localization.PromptGridDescription()}
        </div>
      )}
      {!isLoading &&
        !isFailed &&
        isShowGroupPrompts &&
        groupStatus === "fulfilled" &&
        groups?.length === 0 && (
          <div className={styles.container}>
            <div className={styles.text}>
              {localization.PromptGridNoTeamsSuccessTitle()}
            </div>
            <img
              src="/images/0192-people-l-standard-192x192.webp"
              role="presentation"
              alt=""
            />
            <div
              className={styles.additionalText}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html:
                  localizationWithPlaceholders.PromptGridNoTeamsSuccessDescription(
                    {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      learnMore: `<a href="https://support.microsoft.com/office/create-a-team-from-scratch-in-microsoft-teams-174adf5f-846b-4780-b765-de1a0a737e2b" target="_blank">${localization.PromptGridLearnMoreLinkLabel()}</a>`,
                    }
                  ),
              }}
            ></div>
            <Button
              size="large"
              className={styles.actionButton}
              onClick={onExploreOtherPrompts}
            >
              {localization.PromptGridNoTeamsSuccessButtonLabel()}
            </Button>
          </div>
        )}
      {!isLoading &&
        !isFailed &&
        isShowGroupPrompts &&
        groupStatus === "fulfilled" &&
        groupPromptStatus === "fulfilled" &&
        !!groups &&
        groups?.length !== 0 &&
        groups.some((group) => group.IsOwner) &&
        groupPrompts?.length === 0 && (
          <div className={styles.container}>
            <div className={styles.text}>
              {localization.PromptGridTeamsNoPromptsOwnerSuccessTitle()}
            </div>
            <img
              src="/images/0762-ai-text-l-standard-192x192.webp"
              role="presentation"
              alt=""
            />
            <div className={styles.additionalText}>
              {localization.PromptGridTeamsNoPromptsOwnerSuccessDescription()}
            </div>
            <Button
              size="large"
              className={styles.actionButton}
              onClick={onSharePrompts}
            >
              {localization.PromptGridTeamsNoPromptsOwnerSuccessButtonLabel()}
            </Button>
          </div>
        )}
      {!isLoading &&
        !isFailed &&
        isShowGroupPrompts &&
        groupStatus === "fulfilled" &&
        !!groups &&
        groupPromptStatus === "fulfilled" &&
        !groups.some((group) => group.IsOwner) &&
        groups.some((group) => group.IsMember) &&
        groupPrompts?.length === 0 && (
          <div className={styles.container}>
            <div className={styles.text}>
              {localization.PromptGridTeamsNoPromptsMemberSuccessTitle()}
            </div>
            <img
              src="/images/0762-ai-text-l-standard-192x192.webp"
              role="presentation"
              alt=""
            />
            <div className={styles.additionalText}>
              {localization.PromptGridTeamsNoPromptsMemberSuccessDescription()}
            </div>
            <Button
              size="large"
              className={styles.actionButton}
              onClick={onExploreOtherPrompts}
            >
              {localization.PromptGridTeamsNoPromptsMemberSuccessButtonLabel()}
            </Button>
          </div>
        )}
    </div>
  );
};
